import { Duration } from 'luxon';
import { z } from 'zod';

import { NON_ISO8601_COMPATIBLE_DURATION_PRESETS } from '@/constants';
import type { RotationFrequency } from '@/lib/api/schemas/sublocations';
import { AnchorDuration } from '@/utils/pricing.ts';
import {
  slugSchema,
  zodDurationAllowZero,
  zodPricingItemDuration,
  zodTimeStamp,
  zodTrimmedEmail,
} from '@/utils/zodUtils';

import { ViolationStatusEnum } from './shared/violation-statuses';

export enum PriceModel {
  GLOBAL = 'GLOBAL',
  BLOCK_FACE = 'BLOCKFACE',
}

export enum VehicleType {
  STANDARD = 'standard',
  OVERSIZED = 'oversized',
  TRUCK_TRAILER = 'truck_trailer',
  BOAT_LAUNCH = 'boat',
}

const LegacyViolationPricingScheduleItemSchema = z.object({
  daysSinceViolationConfirmed: z.number().min(0),
  amountDue: z.number().min(0),
  effectiveDate: zodTimeStamp.nullish(),
});

const ViolationPricingScheduleTierSchema = z.object({
  from: z.number().min(0),
  to: z.number().min(0).nullish(),
  amountDue: z.number().min(0),
  effectiveDate: zodTimeStamp.nullish(),
});

export type ViolationPricingScheduleTier = z.infer<
  typeof ViolationPricingScheduleTierSchema
>;

export const VIOLATION_PRICING_SCHEDULE_TYPES = [
  'days_since_confirmed',
  'prior_violations',
] as const;

export type ViolationPricingScheduleType =
  (typeof VIOLATION_PRICING_SCHEDULE_TYPES)[number];

export const VIOLATION_PRICING_TYPE_LABELS: Record<
  ViolationPricingScheduleType,
  string
> = {
  days_since_confirmed: 'Days Since Confirmed',
  prior_violations: 'Prior Violations',
};

export const LOOKBACK_PERIODS = [
  'all_time',
  'last_30_days',
  'last_3_months',
  'last_6_months',
  'last_year',
] as const;

export type LookbackPeriod = (typeof LOOKBACK_PERIODS)[number];

export const LOOKBACK_PERIOD_LABELS: Record<LookbackPeriod, string> = {
  all_time: 'All Time',
  last_30_days: 'Last 30 Days',
  last_3_months: 'Last 3 Months',
  last_6_months: 'Last 6 Months',
  last_year: 'Last Year',
};

const LookbackPeriodSchema = z.enum(LOOKBACK_PERIODS);

const PriorViolationCriteriaSchema = z.object({
  period: LookbackPeriodSchema,
  statuses: z.array(ViolationStatusEnum).optional(),
});

export type PriorViolationCriteria = z.infer<
  typeof PriorViolationCriteriaSchema
>;

const ViolationPricingScheduleObjectSchema = z.object({
  type: z.enum(VIOLATION_PRICING_SCHEDULE_TYPES),
  tiers: z.array(ViolationPricingScheduleTierSchema),
  prior_violation_criteria: PriorViolationCriteriaSchema.nullish(),
});

export type ViolationPricingSchedule = z.infer<
  typeof ViolationPricingScheduleObjectSchema
>;

export const ViolationPricingScheduleSchema = z
  .union([
    ViolationPricingScheduleObjectSchema,
    z.array(LegacyViolationPricingScheduleItemSchema),
  ])
  .nullish()
  .transform((val): ViolationPricingSchedule | null | undefined => {
    if (val == null) return val;
    if (Array.isArray(val)) {
      const sorted = [...val].sort(
        (a, b) => a.daysSinceViolationConfirmed - b.daysSinceViolationConfirmed
      );
      return {
        type: 'days_since_confirmed',
        tiers: sorted.map((item, idx, arr) => ({
          from: item.daysSinceViolationConfirmed,
          to:
            idx < arr.length - 1
              ? arr[idx + 1].daysSinceViolationConfirmed
              : null,
          amountDue: item.amountDue,
          effectiveDate: item.effectiveDate,
        })),
      };
    }
    return val;
  });

export const ViolationReasonSchema = z.object({
  name: z.string(),
  code: z.string().optional(),
  price: z.number().optional(),
  discounted_price: z.number().optional(),
  hidden: z.boolean().default(false),
  bypass_chalking: z.boolean().default(false),
  bypass_active_sessions: z.boolean().default(false),
  requires_external_notes: z.boolean().default(false),
  pricing_schedule: ViolationPricingScheduleSchema,
});

export type ViolationReasonType = z.infer<typeof ViolationReasonSchema>;

export const VoidReasonSchema = z.object({
  name: z.string(),
  value: z.string(),
});

export type VoidReasonType = z.infer<typeof VoidReasonSchema>;

export const ChalkReasonSchema = z.object({
  label: z.string(),
  duration: z.string(),
});

export const HearingOfficerFeeSchema = z.object({
  label: z.string().min(1).max(100),
  amount: z.number().int().nonnegative(),
});

export type HearingOfficerFee = z.infer<typeof HearingOfficerFeeSchema>;

const DarkHoursSchema = z.object({
  start_time: z.string(),
  end_time: z.string(),
});

export type DarkHoursType = z.infer<typeof DarkHoursSchema>;

export enum Weekday {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  [Weekday.MONDAY]: 'Monday',
  [Weekday.TUESDAY]: 'Tuesday',
  [Weekday.WEDNESDAY]: 'Wednesday',
  [Weekday.THURSDAY]: 'Thursday',
  [Weekday.FRIDAY]: 'Friday',
  [Weekday.SATURDAY]: 'Saturday',
  [Weekday.SUNDAY]: 'Sunday',
};

const NonEnforceableHoursSchema = z.object({
  weekday: z.nativeEnum(Weekday),
  start_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Time must be in HH:MM format (24-hour)',
  }),
  end_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Time must be in HH:MM format (24-hour)',
  }),
});

export type NonEnforceableHoursType = z.infer<typeof NonEnforceableHoursSchema>;

export enum EnforcementWorkflowKind {
  REGULAR = 'regular',
  COURTWARE = 'courtware',
}

// SMS Text Reminder Configuration Schemas
const MESSAGE_TYPES = ['initial', 'reminder', 'final'] as const;

const GO_DURATION_SEGMENT =
  '(?:\\d+(?:\\.\\d+)?|\\.\\d+)(?:ns|us|\\u00b5s|ms|s|m|h)';
const GO_DURATION_REGEX = new RegExp(
  `^${GO_DURATION_SEGMENT}(?:${GO_DURATION_SEGMENT})*$`
);

const convertMinutesToDuration = (minutes: number): string => {
  if (minutes <= 0) return '0s';
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0 && remainingMinutes > 0) return `${hours}h${remainingMinutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${remainingMinutes}m`;
};

const ReminderStepSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      return value;
    const step = value as Record<string, unknown>;
    // Convert deprecated delay_from_start_minutes to delay string
    if (!step.delay && typeof step.delay_from_start_minutes === 'number') {
      return {
        ...step,
        delay: convertMinutesToDuration(step.delay_from_start_minutes),
      };
    }
    // Handle legacy steps where both delay and delay_from_start_minutes are omitted
    // (Go's omitempty drops delay:"" and delay_from_start_minutes:0)
    if (!step.delay) {
      return { ...step, delay: '0s' };
    }
    return value;
  },
  z.object({
    delay: z.string().regex(GO_DURATION_REGEX, {
      message:
        'Delay must be in Go duration format (e.g., 0s, 30m, 1.5h, 1h30m)',
    }),
    message_type: z.enum(MESSAGE_TYPES, {
      errorMap: () => ({
        message: 'Message type must be initial, reminder, or final',
      }),
    }),
  })
);

const ReminderScheduleSchema = z.object({
  steps: z.array(ReminderStepSchema).min(1, {
    message: 'At least one reminder step is required',
  }),
});

const DEFAULT_TEXT_REMINDER_STEPS = [
  { delay: '0s', message_type: 'initial' as const },
  { delay: '12h', message_type: 'reminder' as const },
  { delay: '24h', message_type: 'final' as const },
];

const DEFAULT_TEXT_REMINDER_SCHEDULE = {
  steps: DEFAULT_TEXT_REMINDER_STEPS,
};

const DEFAULT_TEXT_REMINDER_CONFIG = {
  enabled: false,
  max_phone_number_age_days: 90,
  plate_masking_enabled: false,
  schedule: DEFAULT_TEXT_REMINDER_SCHEDULE,
};

const TextReminderConfigSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    const config = value as Record<string, unknown>;

    // Hidden SMS fields should not block form saves when reminders are disabled.
    if (config.enabled !== true) {
      return {
        ...config,
        max_phone_number_age_days:
          DEFAULT_TEXT_REMINDER_CONFIG.max_phone_number_age_days,
        schedule: DEFAULT_TEXT_REMINDER_CONFIG.schedule,
      };
    }

    return value;
  },
  z
    .object({
      enabled: z.boolean().default(false),
      max_phone_number_age_days: z.coerce
        .number()
        .int()
        .min(1, { message: 'Must be at least 1 day' })
        .max(365, { message: 'Must be at most 365 days' })
        .default(DEFAULT_TEXT_REMINDER_CONFIG.max_phone_number_age_days),
      plate_masking_enabled: z.boolean().default(false),
      schedule: ReminderScheduleSchema.default(DEFAULT_TEXT_REMINDER_SCHEDULE),
    })
    .default(DEFAULT_TEXT_REMINDER_CONFIG)
);

export type TextReminderConfigType = z.infer<typeof TextReminderConfigSchema>;

// Every field is unconditionally required at the schema level, even when the
// parent alert is disabled. Matches the API binding (ViolatorCriterion fields
// are `required,min=1` on the Go side regardless of RepeatViolatorAlert.Enabled),
// and prevents save-and-fail-on-API cycles: if a criterion row exists, it must
// be fully specified. To "draft" without saving incomplete state, the operator
// deletes the row.
const ViolatorCriterionSchema = z.object({
  label: z
    .string()
    .min(1, { message: 'Label is required' })
    .max(50, { message: 'Label must be 50 characters or fewer' }),
  threshold: z.coerce
    .number()
    .int()
    .min(1, { message: 'Threshold must be at least 1' })
    .max(999, { message: 'Threshold must be at most 999' }),
  period_days: z.coerce
    .number()
    .int()
    .min(0, { message: 'Period must be 0 or more days' })
    .default(0),
  statuses: z
    .array(ViolationStatusEnum)
    .min(1, { message: 'Select at least one status' }),
  emails: z
    .array(zodTrimmedEmail)
    .min(1, { message: 'Add at least one recipient' }),
});

export type ViolatorCriterion = z.infer<typeof ViolatorCriterionSchema>;

const RepeatViolatorAlertSchema = z.object({
  enabled: z.boolean().default(false),
  criteria: z.array(ViolatorCriterionSchema).default([]),
});

const IssuingAuthoritySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  address: z.string().min(1, 'Address is required'),
  phone: z.string().min(1, 'Phone is required'),
});

export const CitationTemplateSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  issuing_authority: IssuingAuthoritySchema.nullable().default(null),
  show_amount: z.boolean().default(true),
  show_qr_code: z.boolean().default(true),
  show_external_notes: z.boolean().default(false),
  show_block_face: z.boolean().default(false),
  payment_url: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().url('Must be a valid URL').nullable().default(null)
  ),
  footer_text: z
    .string()
    .max(2000, 'Footer text must be 2000 characters or fewer')
    .default(''),
  payment_info: z.preprocess(
    (val) => (val === '' ? null : val),
    z
      .string()
      .max(4000, 'Payment info must be 4000 characters or fewer')
      .nullable()
      .default(null)
  ),
});

export const WARNING_POLICY_CONFLICT_MESSAGE =
  'Choose one warning policy: either "First Violation Is a Warning" or "Warnings Only", not both.';

const EnforcementSchema = z
  .object({
    enabled: z.boolean(),
    warn_on_first_violation: z.boolean().default(false),
    issue_warnings_only: z.boolean().optional(),
    violation_reasons: z.array(ViolationReasonSchema).optional(),
    void_reasons: z.array(VoidReasonSchema).optional(),
    chalk_reasons: z.array(ChalkReasonSchema).optional(),
    discount_valid_days: z.number().default(0),
    mailing_fee_amount: z.number().default(0),
    // Business-day cap for mailing camera-enforced municipal notices; null = no cap.
    notice_mailing_cap_business_days: z.preprocess(
      (val) => (val === '' ? null : val),
      z.coerce.number().int().min(1).max(60).nullable().optional()
    ),
    hearing_officer_fees: z.array(HearingOfficerFeeSchema).nullish(),
    peo_chalk_required: z.boolean().default(false),
    bypass_chalking_enabled: z.boolean().default(false),
    warnings_enabled: z.boolean().default(false),
    issue_warnings_as_default: z.boolean().default(false),
    bypass_one_free_per_day_check: z.boolean().default(false),
    arbitration_enabled: z.boolean().default(false),
    auto_confirm_camera_events: z.boolean().optional(),
    tax_rate: z.coerce.number().min(0).max(100).nullable().default(0),
    non_enforceable_hours: z.array(NonEnforceableHoursSchema).optional(),
    repeat_violator_alert: RepeatViolatorAlertSchema.nullable().default(null),
    citation_template: CitationTemplateSchema.nullable().default(null),
    // Id of the coded print layout for portal-printed citations. Left absent
    // when the API never stored one, so a save does not write it.
    citation_print_template: z.string().nullish(),
    workflow: z
      .preprocess((val) => {
        // Handle empty string, null, or undefined by converting to default
        if (val === '' || val === null || val === undefined) {
          return EnforcementWorkflowKind.REGULAR;
        }
        return val;
      }, z.nativeEnum(EnforcementWorkflowKind))
      .default(EnforcementWorkflowKind.REGULAR),
    // Daily violation limit: one per vehicle per day (same reason)
    limit_violation_issuance_by_reason: z.boolean().default(false),
    bypass_daily_violation_limit_check: z.boolean().default(false),
    daily_violation_limit_mode: z.preprocess(
      (val) =>
        val === '' || val === null || val === undefined ? 'block' : val,
      z.enum(['block', 'warn'])
    ),
    daily_violation_limit_window: z.preprocess(
      (val) =>
        val === '' || val === null || val === undefined ? 'rolling_24h' : val,
      z.enum(['rolling_24h', 'calendar_day'])
    ),
  })
  .superRefine((enforcement, ctx) => {
    // Ensure warn_on_first_violation and issue_warnings_only are mutually exclusive
    if (
      enforcement.warn_on_first_violation &&
      enforcement.issue_warnings_only
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: WARNING_POLICY_CONFLICT_MESSAGE,
        path: ['warn_on_first_violation'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: WARNING_POLICY_CONFLICT_MESSAGE,
        path: ['issue_warnings_only'],
      });
    }

    if (
      enforcement.issue_warnings_as_default === true &&
      enforcement.warnings_enabled !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          '"Open in Warning Mode by Default" needs "Officers Can Issue Warnings" turned on.',
        path: ['issue_warnings_as_default'],
      });
    }

    // Validate no duplicate weekdays in non_enforceable_hours
    if (
      enforcement.non_enforceable_hours &&
      enforcement.non_enforceable_hours.length > 0
    ) {
      const weekdaySeen = new Set<string>();
      enforcement.non_enforceable_hours.forEach((hours, index) => {
        if (weekdaySeen.has(hours.weekday)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate weekday: ${hours.weekday}. Each weekday can only appear once.`,
            path: ['non_enforceable_hours', index, 'weekday'],
          });
        }
        weekdaySeen.add(hours.weekday);
      });
    }

    // Enabled alerts need at least one criterion. Per-criterion required-field
    // validation (statuses, emails, label, threshold) is handled at the field
    // level via ViolatorCriterionSchema.
    if (enforcement.repeat_violator_alert?.enabled) {
      const alert = enforcement.repeat_violator_alert;
      if (!alert.criteria || alert.criteria.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Add at least one criterion when repeat violator alerts are enabled',
          path: ['repeat_violator_alert', 'criteria'],
        });
      }
    }
  });

export enum BillingSettingsInterval {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export const BillingSettingsSchema = z
  .object({
    enabled: z.boolean(),
    label: z.string().optional().default(''),
    platform_cut: z
      .object({
        ach_cut: z.coerce.number().nullish(),
        standard_cut: z.coerce.number().nullish(),
      })
      .nullish(),
    subscriptionBasePrice: z.coerce.number().optional(),
    termsAndConditionsHTML: z.string().optional(),
    isNotRenewable: z.boolean().default(false),
    requireTrailerPlate: z.boolean().default(false),
    interval: z
      .nativeEnum(BillingSettingsInterval)
      .or(z.literal(''))
      .default(BillingSettingsInterval.MONTHLY),
    verified_rate: z
      .object({
        label: z.string().min(1),
        subscriptionBasePrice: z.coerce.number().positive(),
        platform_cut: z
          .object({
            ach_cut: z.coerce.number().nullish(),
            standard_cut: z.coerce.number().nullish(),
          })
          .nullish(),
        termsAndConditionsHTML: z.string().optional().default(''),
      })
      .nullish(),
  })
  .superRefine((billing, ctx) => {
    if (billing.enabled && !billing.subscriptionBasePrice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Subscription base price is required when billing is enabled',
        path: ['subscriptionBasePrice'],
      });
    }

    if (billing.enabled && !billing.termsAndConditionsHTML) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Terms and conditions HTML is required when billing is enabled',
        path: ['termsAndConditionsHTML'],
      });
    }

    if (billing.enabled && !billing.platform_cut?.ach_cut) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'ACH cut is required when billing is enabled',
        path: ['platform_cut', 'ach_cut'],
      });
    }

    if (billing.enabled && !billing.platform_cut?.standard_cut) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Standard cut is required when billing is enabled',
        path: ['platform_cut', 'standard_cut'],
      });
    }

    if (billing.enabled && billing.verified_rate) {
      if (!billing.verified_rate.platform_cut?.ach_cut) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ACH cut is required for the verified rate',
          path: ['verified_rate', 'platform_cut', 'ach_cut'],
        });
      }
      if (!billing.verified_rate.platform_cut?.standard_cut) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Standard cut is required for the verified rate',
          path: ['verified_rate', 'platform_cut', 'standard_cut'],
        });
      }
    }
  });

export const DOCUMENT_TYPE_OPTIONS = [
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'government_id', label: 'Government ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'utility_bill', label: 'Utility Bill' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'lease_agreement', label: 'Lease Agreement' },
] as const;

export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
export const STATE_CODE_REGEX = /^[A-Za-z]{2}$/;

export const AddressVerificationSchema = z
  .object({
    enabled: z.boolean().default(false),
    eligible_city: z.string().default(''),
    eligible_state: z.string().default(''),
    eligible_zip_codes: z.array(z.string()).optional().default([]),
    municipal_boundary_name: z.string().optional().default(''),
    municipal_boundary_state_code: z.string().max(2).optional().default(''),
    persona_template_id: z.string().default(''),
    verification_validity_days: z.coerce.number().min(1).max(3650).default(365),
    accepted_document_types: z.array(z.string()).optional().default([]),
  })
  .superRefine((av, ctx) => {
    if (!av.enabled) return;

    if (!av.persona_template_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Persona template ID is required when address verification is enabled',
        path: ['persona_template_id'],
      });
    }

    if (!av.eligible_city) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Eligible city is required when address verification is enabled',
        path: ['eligible_city'],
      });
    }

    if (!av.eligible_state) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Eligible state is required when address verification is enabled',
        path: ['eligible_state'],
      });
    }

    if (av.eligible_state && !STATE_CODE_REGEX.test(av.eligible_state.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Eligible state must be a 2-letter abbreviation',
        path: ['eligible_state'],
      });
    }

    if (
      av.municipal_boundary_state_code &&
      !STATE_CODE_REGEX.test(av.municipal_boundary_state_code.trim())
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Boundary state code must be a 2-letter abbreviation',
        path: ['municipal_boundary_state_code'],
      });
    }

    const invalidZip = av.eligible_zip_codes?.find(
      (zip) => !ZIP_CODE_REGEX.test(zip.trim())
    );
    if (invalidZip) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Eligible ZIP codes must be valid (12345 or 12345-6789)',
        path: ['eligible_zip_codes'],
      });
    }
  });

type PresetType =
  (typeof NON_ISO8601_COMPATIBLE_DURATION_PRESETS)[number]['durationForApi'];
type PriceItemDurationType = Duration<true> | PresetType | AnchorDuration;

export const isPricingItemDurationLuxonInstance = (
  d: PriceItemDurationType
): d is Duration<true> => {
  return d instanceof Duration;
};

export const PriceItemSchema = z
  .object({
    duration: zodPricingItemDuration,
    price: z.number(),
    description: z.string().nullish(),
    label: z.string().nullish(),
    vehicle_type: z.nativeEnum(VehicleType).optional(),
    base_charge: z.number().min(0).optional(),
    max_charge: z.number().min(0).optional(),
    // Only list-type items may carry this flag; absent or null means enabled
    // (API contract), so null must not fail validation.
    enabled: z.boolean().nullish(),
  })
  .refine(
    (pricingItem) => {
      if (!pricingItem.max_charge || !pricingItem.base_charge) {
        return true;
      }

      return pricingItem.max_charge > pricingItem.base_charge;
    },
    {
      message: 'max charge should always be greater than base charge',
      path: ['max_charge'],
    }
  );

const TimeRangeSchema = z.object({
  start_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Time must be in HH:MM format (24-hour)',
  }),
  end_time: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, {
    message: 'Time must be in HH:MM format (24-hour)',
  }),
});

const baseIncrementalPricingSchema = z.object({
  type: z.literal('increment'),
  items: z.array(PriceItemSchema).min(1).max(1),
});
const baseListPricingSchema = z.object({
  type: z.literal('list'),
  items: z.array(PriceItemSchema).min(1),
});
const wholeHourDuration = zodDurationAllowZero.refine(
  (d) => d instanceof Duration && d.as('hours') === Math.round(d.as('hours')),
  { message: 'Tiered durations must be whole hours' }
);

const TieredPriceItemSchema = z.object({
  duration: zodPricingItemDuration,
  price: z.number().min(0),
  from_duration: wholeHourDuration,
  to_duration: wholeHourDuration.nullish(),
  description: z.string().nullish(),
  label: z.string().nullish(),
});
const baseTieredPricingSchema = z.object({
  type: z.literal('tiered'),
  items: z.array(TieredPriceItemSchema).min(1),
  align_to_dark_hours: z.boolean().nullish(),
});

const BasePricingSchema = z.discriminatedUnion('type', [
  baseIncrementalPricingSchema,
  baseListPricingSchema,
]);

const TimeVariantSchema = z.object({
  time_range: TimeRangeSchema,
  pricing: BasePricingSchema,
});

const timeBasedPricingSchema = z.object({
  type: z.literal('time_based'),
  time_variants: z.array(TimeVariantSchema).min(1),
});

export const PricingSchema = z.discriminatedUnion('type', [
  baseIncrementalPricingSchema,
  baseListPricingSchema,
  timeBasedPricingSchema,
  baseTieredPricingSchema,
]);

const WeekdayPricingSchema = z.discriminatedUnion('type', [
  baseIncrementalPricingSchema,
  baseListPricingSchema,
  timeBasedPricingSchema,
]);

const WeekdayRules = z.object({
  monday: WeekdayPricingSchema.optional(),
  tuesday: WeekdayPricingSchema.optional(),
  wednesday: WeekdayPricingSchema.optional(),
  thursday: WeekdayPricingSchema.optional(),
  friday: WeekdayPricingSchema.optional(),
  saturday: WeekdayPricingSchema.optional(),
  sunday: WeekdayPricingSchema.optional(),
});

export const PresalePricingSchema = z.object({
  type: z.literal('list').optional().nullable(),
  items: z
    .array(
      z.object({
        description: z.string(),
        duration: z.string().nullable(),
        price: z.number(),
        capacity: z.number().optional().nullable(),
      })
    )
    .optional()
    .nullable(),
});

export const RequirementsSchema = z.object({
  license_plate: z.boolean().optional().nullable(),
});

export const PresaleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  starts_at: zodTimeStamp,
  ends_at: zodTimeStamp,
  pricing: PresalePricingSchema,
  requirements: RequirementsSchema,
  convenience_fee: z.number().nullish(),
});

const weekdaysArraySchema = z.array(
  z.discriminatedUnion('type', [
    baseIncrementalPricingSchema.merge(
      z.object({ weekDay: WeekdayRules.keyof() })
    ),
    baseListPricingSchema.merge(z.object({ weekDay: WeekdayRules.keyof() })),
    timeBasedPricingSchema.merge(z.object({ weekDay: WeekdayRules.keyof() })),
  ])
);

export const ValetPricingItemSchema = z
  .object({
    description: z.string().trim().min(1, {
      message: 'Description is required.',
    }),
    id: z.string(),
    price: z.number().positive(),
    qr_code_path: z.string().nullish(),
    are_tip_presets_enabled: z.boolean(),
    tip_presets: z
      .array(
        z
          .number()
          .positive()
          .or(z.object({ value: z.number().positive() }))
      )
      .max(3, { message: 'There can be maximum 3 presets per pricing option.' })
      .nullable()
      .transform((v) => v || [])
      .transform((v) =>
        v.map((innerV) => {
          if (typeof innerV === 'object') {
            return innerV;
          }
          return { value: innerV };
        })
      ),
  })
  .superRefine((val, ctx) => {
    if (val.are_tip_presets_enabled && !val.tip_presets?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tip presets shouldn't be empty",
        path: ['tip_presets'],
      });
    }
  });

export const ValetPricingSchema = z.object({
  items: z.array(ValetPricingItemSchema).min(0),
});

export const PricingScheduleSchema = z.object({
  location_id: z.string(),
  block_face_id: z.string().uuid().nullish(),
  version: z.number(),
  weekday_rules: WeekdayRules.or(weekdaysArraySchema)
    .nullable()
    .transform<z.infer<typeof weekdaysArraySchema>>((weekDayRules) => {
      if (Array.isArray(weekDayRules)) return weekDayRules;
      if (!weekDayRules) return [];

      return Object.entries(weekDayRules)
        .filter(
          (entry): entry is [string, NonNullable<(typeof entry)[1]>] =>
            entry[1] != null
        )
        .map(([weekDay, pricingSchema]) => {
          return { ...pricingSchema, weekDay: weekDay as WeekDay } as const;
        });
    }),
  special_dates: z
    .record(z.string(), PricingSchema)
    .nullable()
    .transform((specialDates) => specialDates ?? {}),
  presales: z.array(PresaleSchema).nullish(),
  default_pricing: PricingSchema.nullable(),
  valet_pricing: ValetPricingSchema.nullable(),
  updated_at: zodTimeStamp.nullable(),
});

export type WeekDay = keyof z.infer<typeof WeekdayRules>;

export type Pricing = z.infer<typeof PricingSchema>;
export type PricingSchedule = z.infer<typeof PricingScheduleSchema>;
export type WeekdayRules = z.infer<typeof WeekdayRules>;
export type TimeRange = z.infer<typeof TimeRangeSchema>;
export type TimeVariant = z.infer<typeof TimeVariantSchema>;

export const PointSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});
export const PolygonSchema = z.object({
  vertices: z.array(PointSchema),
});
// The API stores a block face schedule only with default pricing, so weekday
// or event overrides without it would be lost on save. Annotated explicitly:
// the refined inferred type is too large for declaration emit.
const BlockFacePricingScheduleSchema: z.ZodType<
  z.output<typeof PricingScheduleSchema>,
  z.ZodTypeDef,
  z.input<typeof PricingScheduleSchema>
> = PricingScheduleSchema.superRefine((schedule, ctx) => {
  const hasOtherPricing =
    schedule.weekday_rules.length > 0 ||
    Object.keys(schedule.special_dates).length > 0;
  if (schedule.default_pricing == null && hasOtherPricing) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['default_pricing'],
      message: 'Set up default pricing before adding weekday or event pricing.',
    });
  }
});

export const BlockFaceSchema = z.object({
  // List endpoints may return partial block face payloads.
  polygon: PolygonSchema.nullish(),
  block_face_id: z.string().uuid().nullish(),
  // Legacy/alternate payload shapes seen in older list endpoints.
  id: z.string().uuid().nullish(),
  blockface_address: z.string().optional(),
  address: z.string().optional(),
  blockface_label: z.string().optional(),
  label: z.string().optional(),
  grace_period_mins: z.number().int().nonnegative().nullish(),
  // Block-face override of the location-level mailing cap; null = inherit.
  notice_mailing_cap_business_days: z.preprocess(
    (val) => (val === '' ? null : val),
    z.coerce.number().int().min(1).max(60).nullable().optional()
  ),
  pricing_schedule: BlockFacePricingScheduleSchema.nullish(),
  pricing: PricingSchema.nullish(),
  dark_hours: z.array(DarkHoursSchema).nullish(),
  violation_reasons: z.array(ViolationReasonSchema).nullish(),
  fee_label: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().max(100).nullable().optional()
  ),
  session_label: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().max(100).nullable().optional()
  ),
  blockface_description: z.preprocess(
    (val) => (val === '' ? null : val),
    z.string().max(255).nullable().optional()
  ),
  parkpliant_lot_code: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() || null : val),
    z.string().min(1).max(50).nullable().optional()
  ),
  parkpliant_group_id: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() || null : val),
    z.string().uuid().nullable().optional()
  ),
  hearing_officer_fees: z.array(HearingOfficerFeeSchema).nullish(),
});
export const BlockFaceListSchema = z.array(BlockFaceSchema);
export type BlockFaceType = z.infer<typeof BlockFaceSchema>;

export const ValidationPricingSchema = z.object({
  type: z.literal('list'),
  // Validation pricing has no per-item toggle, so every option is forced active.
  items: z.array(
    PriceItemSchema.transform(
      ({ enabled: _enabled, ...rest }): PriceItemType => ({
        ...rest,
        enabled: true,
      })
    )
  ),
});

export const ValidationSettingsSchema = z.object({
  validation_pricing: ValidationPricingSchema.nullable(),
  validation_fee: z.number().nonnegative().nullable(),
  require_paid_session: z.boolean().optional(),
  validation_scope: z.enum(['location', 'block_face']).optional(),
});

export enum LocationKind {
  REGULAR = 'REGULAR',
  VALET = 'VALET',
}

export const ESCALATION_CONTACTS_MAX = 20;

export const ESCALATION_CONTACT_RECIPIENTS = ['to', 'cc'] as const;

export type EscalationContactRecipient =
  (typeof ESCALATION_CONTACT_RECIPIENTS)[number];

// A contact the server has not saved yet has no id. Every field falls back
// rather than failing, so a stored row is always readable and never lost on
// the next save.
export const EscalationContactSchema = z
  .object({
    id: z
      .union([z.string(), z.number().transform(String)])
      .optional()
      .catch(undefined),
    name: z.string().catch(''),
    role: z.string().catch(''),
    email: z.string().catch(''),
    phone: z.string().catch(''),
    recipient: z.enum(ESCALATION_CONTACT_RECIPIENTS).catch('to'),
    is_primary: z.boolean().catch(false),
  })
  .catch(() => ({
    name: '',
    role: '',
    email: '',
    phone: '',
    recipient: 'to' as const,
    is_primary: false,
  }));

export type EscalationContact = z.infer<typeof EscalationContactSchema>;

export const EscalationContactsSchema = z
  .array(EscalationContactSchema)
  .nullish()
  .catch([]);

const baseLocationSchema = z.object({
  locationId: z.string(), // .brand<'locationID'>() TODO(GLEF1X): replace with branded type
  type: z.nativeEnum(LocationKind).default(LocationKind.REGULAR),
  address: z.string().optional(),
  nickname: z.string().optional(),
  company_name: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
  parkpliant_group_id: z.string().uuid().nullable().optional(),
  timezone: z.string().optional(),
  pricing_schedule: PricingScheduleSchema.optional(),
  processing_fee: z.coerce.number().optional(),
  processing_percent: z.coerce.number().min(0).max(100).optional(),
  convenience_fee: z.number().optional(),
  tax_rate: z.coerce.number().min(0).max(100).optional(),
  test_mode: z.boolean().nullish(),
  quick_permit_enabled: z.boolean().nullish().default(false),
  plate_correction_enabled: z.boolean().nullish().default(false),
  bank_account_id: z.string().optional(),
  cover_convenience_fee: z.boolean().optional(),
  cover_processing_fee: z.boolean().optional(),
  geo_coordinates: z.object({
    lat: z.coerce.number(),
    lon: z.coerce.number(),
  }),
  enforcement: EnforcementSchema.optional(),
  address_verification: AddressVerificationSchema.optional(),
  text_reminder_config: z.preprocess(
    (val) => (val == null ? DEFAULT_TEXT_REMINDER_CONFIG : val),
    TextReminderConfigSchema
  ),
  updated_at: zodTimeStamp.nullish(),
  escalation_contacts: EscalationContactsSchema,
});

const regularLocationSchema = z
  .object({
    price_model: z.nativeEnum(PriceModel).default(PriceModel.GLOBAL),
    max_time: z.coerce.number().optional(),
    min_time: z.coerce.number().optional(),
    presale_convenience_fee: z.coerce.number().optional(),
    validation_settings: ValidationSettingsSchema.optional(),
    capacity: z.coerce.number().optional(),
    unreserved_oversell_percent: z.coerce
      .number()
      .int()
      .min(100, 'Oversell ratio must be at least 100%')
      .max(200, 'Oversell ratio cannot exceed 200%')
      .optional(),
    // Write-only audit note the API requires whenever capacity or oversell changes.
    capacity_change_reason: z.string().trim().max(500).optional(),
    pricing: PricingSchema,
    dark_hours: z.array(DarkHoursSchema).optional(),
    grace_period_mins: z.coerce.number().optional(),
    preauthorized_reservations_enabled: z.boolean().default(false),
    prorate_unused_time_on_end: z.boolean().default(false),
    oversell_threshold_enabled: z.boolean().default(false),
    oversell_threshold_message: z.string().nullish(),
    oversell_threshold_percent: z.coerce.number().nullish(),
    preauthorized_reservation_refund_percent: z.coerce.number().optional(),
    block_faces: BlockFaceListSchema.optional(),
    billing: BillingSettingsSchema,
    show_parking_fee_in_dropdown: z.boolean().optional().default(true),
    show_address_on_checkout: z.boolean().nullish(),
    // True Start fields
    true_start_enabled: z.boolean().default(false),
    show_entry_time_to_customer: z.boolean().default(false),
    true_start_billing_enforcement_enabled: z.boolean().default(false),
    free_parking_duration_mins: z.coerce.number().optional().nullable(),
    // Passive Free Parking field
    passive_free_parking_enabled: z.boolean().default(false),
    // ACH processing fields
    ach_processing_fee: z.coerce.number().int().min(0).default(0),
    ach_processing_percent: z.coerce.number().min(0).max(100).default(0.8),
    // Billing V2 config
    billing_v2: z
      .object({
        enabled: z.boolean().default(false),
        platform_fee_cents: z.coerce.number().int().min(0),
        // When true, the location's Stripe invoices are issued from the
        // operator's connected account (the bank account funds settle to)
        // instead of the platform. One-way latch: once true the API ignores
        // attempts to set it back to false.
        invoice_issuer_from_bank_account: z.boolean().default(false),
        // Late fees on past-due group-agreement invoices. All three are
        // optional on the wire; when omitted the API applies defaults
        // (enabled, 5-day grace, $25.00). Empty inputs coerce to null so a
        // cleared field falls back to the server default. Ranges are enforced
        // in the superRefine below, gated on late_fee_enabled, so a stale value
        // left in a hidden input can never block Save once fees are turned off.
        late_fee_enabled: z.boolean().nullish(),
        late_fee_grace_days: z.preprocess(
          (val) => (val === '' ? null : val),
          z.coerce.number().int().nullish()
        ),
        late_fee_cents: z.preprocess(
          (val) => (val === '' ? null : val),
          z.coerce.number().int().nullish()
        ),
        terms_and_conditions: z
          .object({
            html: z.string(),
            // Server-stamped on change; never sent by the client. Comes back
            // as null when the terms have never been updated.
            updated_at: zodTimeStamp.nullish(),
          })
          .nullish(),
      })
      .superRefine((data, ctx) => {
        // late_fee_enabled is nil-on, so fees are only off when explicitly
        // false. Skip range checks then; otherwise mirror the backend validate
        // tags (gte=1,lte=90 / gte=1,lte=1000000) for any value present.
        if (data.late_fee_enabled === false) {
          return;
        }
        if (
          data.late_fee_grace_days != null &&
          (data.late_fee_grace_days < 1 || data.late_fee_grace_days > 90)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Grace period must be between 1 and 90 days.',
            path: ['late_fee_grace_days'],
          });
        }
        if (
          data.late_fee_cents != null &&
          (data.late_fee_cents < 1 || data.late_fee_cents > 1000000)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Late fee must be between $0.01 and $10,000.00.',
            path: ['late_fee_cents'],
          });
        }
      })
      .nullish(),
    validation_time_gate_minutes: z.coerce
      .number()
      .int()
      .min(0)
      .optional()
      .nullable(),
    dark_hours_alert_title: z.preprocess(
      (val) => (val === '' ? null : val),
      z.string().max(200).nullable().optional()
    ),
    dark_hours_alert_description: z.preprocess(
      (val) => (val === '' ? null : val),
      z.string().max(4000).nullable().optional()
    ),
    dark_hours_restriction_message: z.preprocess(
      (val) => (val === '' ? null : val),
      z.string().max(4000).nullable().optional()
    ),
    free_parking_tracking_mode: z
      .enum(['per_location', 'per_block_face'])
      .optional()
      .nullable(),
  })
  .merge(baseLocationSchema)
  .merge(
    z.object({
      type: z.literal(LocationKind.REGULAR),
    })
  );

const valetLocationSchema = baseLocationSchema.merge(
  z.object({
    type: z.literal(LocationKind.VALET),
  })
);

export const LocationSchema = z.discriminatedUnion('type', [
  regularLocationSchema,
  valetLocationSchema,
]);

export type LocationType = z.infer<typeof LocationSchema>;

export const PARKPLIANT_GROUP_REQUIRED_MESSAGE =
  'A Parkpliant group is required when enforcement is on. Without one, citations reach Parkpliant with no routing and may be mailed under the wrong client. Block-face groups do not cover the location itself.';

type ParkpliantGroupRequirement = {
  type: LocationKind | undefined;
  isEnforcementEnabled: boolean | undefined;
  workflow: EnforcementWorkflowKind | null | undefined;
};

// Parkpliant routes a lot's citations by its group, and a push without one
// still succeeds with no routing. Courtware lots never reach Parkpliant.
export const isParkpliantGroupRequired = ({
  type,
  isEnforcementEnabled,
  workflow,
}: ParkpliantGroupRequirement) =>
  type === LocationKind.REGULAR &&
  isEnforcementEnabled === true &&
  workflow !== EnforcementWorkflowKind.COURTWARE;

const ParkpliantGroupFieldsSchema = z.object({
  type: z.nativeEnum(LocationKind).optional().catch(undefined),
  // The create payload carries the workflow at the top level, a saved location
  // carries it under enforcement.
  workflow: z.nativeEnum(EnforcementWorkflowKind).nullish().catch(undefined),
  enforcement: z
    .object({
      enabled: z.boolean().optional().catch(undefined),
      workflow: z
        .nativeEnum(EnforcementWorkflowKind)
        .optional()
        .catch(undefined),
    })
    .nullish()
    .catch(undefined),
  parkpliant_group_id: z.string().nullish().catch(undefined),
});

type ParkpliantGroupFields = z.infer<typeof ParkpliantGroupFieldsSchema>;

export const PARKPLIANT_GROUP_REQUIRED_FOR_WORKFLOW_MESSAGE =
  'A Parkpliant group is required for the regular workflow. Choose one under Where Tickets Go.';

// Joined onto a form schema with `.and()` so the check still runs while
// unrelated fields are invalid; a refinement on the whole union would wait for
// them. Its fields never fail and it yields no values, so it only adds the
// issue and cannot clash with the base schema's parsed values when merged.
const parkpliantGroupRequirement = (
  isRequired: (fields: ParkpliantGroupFields) => boolean,
  isRequiredByWorkflowSwitch: (fields: ParkpliantGroupFields) => boolean = () =>
    false
) =>
  ParkpliantGroupFieldsSchema.superRefine((fields, ctx) => {
    if (!isRequired(fields) || fields.parkpliant_group_id) {
      return;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PARKPLIANT_GROUP_REQUIRED_MESSAGE,
      path: ['parkpliant_group_id'],
    });
    if (isRequiredByWorkflowSwitch(fields)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: PARKPLIANT_GROUP_REQUIRED_FOR_WORKFLOW_MESSAGE,
        path: ['enforcement', 'workflow'],
      });
    }
  }).transform(() => ({}));

// The location as it was loaded, before the edits being saved.
export type ParkpliantGroupBaseline = {
  wasEnforcementEnabled: boolean;
  hadParkpliantGroup: boolean;
  loadedWorkflow: EnforcementWorkflowKind | null | undefined;
};

export const getParkpliantGroupBaseline = (
  location: LocationType | undefined
): ParkpliantGroupBaseline => ({
  wasEnforcementEnabled: location?.enforcement?.enabled === true,
  hadParkpliantGroup: Boolean(location?.parkpliant_group_id),
  loadedWorkflow:
    location?.type === LocationKind.REGULAR
      ? location.enforcement?.workflow
      : undefined,
});

// Lots that were already enforcing through Parkpliant without a group are
// being remediated separately, so they keep saving their other settings. Any
// other save that leaves the lot enforcing through Parkpliant, such as turning
// enforcement on, switching from courtware to the regular workflow, or
// removing the group from a lot that has one, must keep a group in place.
export const isParkpliantGroupRequiredOnSave = ({
  wasEnforcementEnabled,
  hadParkpliantGroup,
  loadedWorkflow,
  ...requirement
}: ParkpliantGroupRequirement & ParkpliantGroupBaseline) => {
  const wasParkpliantEnforced =
    wasEnforcementEnabled &&
    loadedWorkflow !== EnforcementWorkflowKind.COURTWARE;
  return (
    !(wasParkpliantEnforced && !hadParkpliantGroup) &&
    isParkpliantGroupRequired(requirement)
  );
};

// Validates a location config before it is saved, given the location as it
// was loaded. LocationSchema stays unrefined so lots without a group still
// load.
export const getLocationFormSchema = (baseline: ParkpliantGroupBaseline) =>
  LocationSchema.and(
    parkpliantGroupRequirement(
      ({ type, enforcement }) =>
        isParkpliantGroupRequiredOnSave({
          ...baseline,
          type,
          isEnforcementEnabled: enforcement?.enabled,
          workflow: enforcement?.workflow,
        }),
      ({ enforcement }) =>
        baseline.loadedWorkflow === EnforcementWorkflowKind.COURTWARE &&
        enforcement?.workflow !== EnforcementWorkflowKind.COURTWARE
    )
  );

export type RegularLocationType = Extract<
  z.infer<typeof LocationSchema>,
  { type: LocationKind.REGULAR }
>;

export type ViolationReasons = NonNullable<
  NonNullable<RegularLocationType['enforcement']>['violation_reasons']
>;

export type ChalkReasons = NonNullable<
  NonNullable<RegularLocationType['enforcement']>['chalk_reasons']
>;

export type EnforcementType = RegularLocationType['enforcement'];

export type CitationTemplateType = z.infer<typeof CitationTemplateSchema>;

export const CreateLocationBase = z.object({
  // location info
  locationId: slugSchema({ field: 'Location ID' }),
  type: z.nativeEnum(LocationKind).default(LocationKind.REGULAR),
  address: z.string(),
  geo_coordinates: z.object({
    lat: z.coerce.number(),
    lon: z.coerce.number(),
  }),
  nickname: z.string().optional(),
  timezone: z.string().optional(),
  // Fee and pricing info
  processing_fee: z.number(),
  convenience_fee: z.number(),
  processing_percent: z
    .number()
    .min(0, { message: 'Value should be at least 0' })
    .max(100, { message: 'Value should be less than or equal to 100' }),
  tax_rate: z
    .number()
    .max(100, { message: 'Value should be less than or equal to 100' })
    .optional(),
  test_mode: z.boolean().nullish(),
  // bank account
  bank_account_id: z.string(),
  pricing_schedule: PricingScheduleSchema.pick({
    default_pricing: true,
  }).optional(),
  cover_convenience_fee: z.boolean().optional(),
  cover_processing_fee: z.boolean().optional(),
  workflow: z
    .nativeEnum(EnforcementWorkflowKind)
    .default(EnforcementWorkflowKind.REGULAR)
    .optional(),
});

const createRegularLocationSchema = CreateLocationBase.merge(
  z.object({
    type: z.literal(LocationKind.REGULAR),
    // Settings
    capacity: z.number(),
    grace_period_mins: z.number().optional(),
    validation_fee: z.number(),
    min_time: z.number(),
    max_time: z.number(),
    enforcement: z
      .object({
        enabled: z.boolean().optional(),
        warn_on_first_violation: z.boolean().optional(),
        issue_warnings_only: z.boolean().optional(),
      })
      .optional(),
    oversell_threshold_enabled: z.boolean(),
    oversell_threshold_message: z.string().optional(),
    oversell_threshold_percent: z.number().optional(),
    pricing: PricingSchema,
    preauthorized_reservations_enabled: z.boolean().optional(),
    preauthorized_reservation_refund_percent: z.number().optional(),
    company_name: z
      .string()
      .trim()
      .min(3, { message: 'Company name must be at least 3 characters long.' }),
    parkpliant_group_id: z.string().uuid().nullable().optional(),
  })
);

const createValetLocationSchema = CreateLocationBase.merge(
  z.object({
    type: z.literal(LocationKind.VALET),
    pricing_schedule: z.object({
      valet_pricing: ValetPricingSchema,
    }),
  })
);

export const CreateLocationSchema = z
  .discriminatedUnion('type', [
    createValetLocationSchema,
    createRegularLocationSchema,
  ])
  .and(
    parkpliantGroupRequirement(({ type, enforcement, workflow }) =>
      isParkpliantGroupRequired({
        type,
        isEnforcementEnabled: enforcement?.enabled,
        workflow: workflow ?? enforcement?.workflow,
      })
    )
  );

export const RegularLocationListItemSchema = regularLocationSchema.merge(
  z.object({
    // NOTE: location config list endpoint no longer returns pricing
    pricing: z.any(),
  })
);

export const LocationListItemSchema = z.discriminatedUnion('type', [
  RegularLocationListItemSchema,
  valetLocationSchema.merge(
    z.object({
      // NOTE: location config list endpoint no longer returns pricing
      pricing: z.any(),
    })
  ),
]);
export const LocationList = z.array(LocationListItemSchema);

export type LocationListType = z.infer<typeof LocationList>;

export type PriceItemType = z.infer<typeof PriceItemSchema>;
export type TieredPriceItemType = z.infer<typeof TieredPriceItemSchema>;

export type CreateLocationSchemaType = z.infer<typeof CreateLocationSchema>;
// TODO: make it compliant with the API

export const CreateLocationPayloadSchema = z.discriminatedUnion('type', [
  createValetLocationSchema
    .omit({ locationId: true })
    .merge(z.object({ location_id: z.string() })),
  createRegularLocationSchema
    .merge(
      z.object({
        enforcement: z.boolean().optional(),
        // The create endpoint reads the workflow from `enforcement_workflow`.
        enforcement_workflow: z.nativeEnum(EnforcementWorkflowKind).optional(),
        warn_on_first_violation: z.boolean().optional(),
        preauthorized_reservation_refund_percent: z.number().optional(),
      })
    )
    .omit({ locationId: true, workflow: true })
    .merge(z.object({ location_id: z.string() })),
]);

export type CreateLocationPayloadSchemaType = z.infer<
  typeof CreateLocationPayloadSchema
>;

export type UpdateLocationParams = {
  locationId: string;
  sublocations?: UpdateSubLocationParams[];
  validationRotateAccessCode?: boolean;
};

export type UpdateSubLocationParams = {
  subLocationId: string;
  duration?: string | Duration; // chrono.Duration is likely a string (ISO 8601 duration format)
  approvalCode?: string;
  displayName?: string;
  validationAccessCode?: string;
  validationAccessCodeDistributionList?: string[];
  validationAccessCodeSMSDistributionList?: string[];
  blockFaceId?: string | null;
  billing?: UpdateBillingParams;
  rotationSchedule?: {
    enabled: boolean;
    frequency: RotationFrequency;
    day_of_week?: number;
    time?: string;
  };
  validationTimeGateMinutes?: number | null;
  // Sublocation-scoped ceiling (minutes) used exclusively by the usage-based
  // validation flow. Send `null` to clear; omit to leave unchanged. Decoupled
  // from Location.max_time on purpose -- see SubLocationSchema.maxValidationMins.
  maxValidationMins?: number | null;
  maxValidationsPerEightHours?: number | null;
};

export type UpdateBillingParams = {
  enabled?: boolean;
  is_billing_required?: boolean;
  monthly_saas_fee?: number;
  monthly_quantity?: number | null;
  is_waiving_parking_fee?: boolean;
  payment_method?: SetupPaymentMethod;
  // Mirrors the BillingTypeSchema enum on the backend (count | usage).
  // SubLocationUpdateForm spreads `formData.billing` -- which already
  // carries this -- into the payload, so omitting it from this type was
  // a type-safety gap rather than a missing field on the wire. Listed
  // explicitly so future callers (e.g. SublocationCreationDialog) get
  // intellisense + a compile error when they try to send a usage-only
  // billing shape without flipping this.
  billing_type?: 'count' | 'usage';
};

export type SetupPaymentMethod = {
  setup_intent_id: string;
  client_secret: string;
  customer_session_client_secret?: string;
};
